import * as yup from 'yup';
import { render } from '../utils/templateUtils';
import type { Translations } from '../i18n/types';

/**
 * Латиница, цифры и типовая пунктуация — без кириллицы и других алфавитов.
 * Квантификатор `*` оставлен намеренно: пустое значение отсеет `required()`,
 * иначе пользователь увидел бы сразу две ошибки на одном поле.
 */
const LATIN_TEXT_REGEX = /^[A-Za-z0-9\s.,:&()"'’/+_-]*$/;

/** Латиница для ФИО: буквы, пробел, апостроф, точка и дефис. */
const LATIN_NAME_REGEX = /^[A-Za-z\s.'’-]*$/;

export const createRegistrationEmailSchema = (t: Translations) =>
    yup.object({
        email: yup
            .string()
            .required(t.registration.validation.emailRequired)
            .email(t.registration.validation.emailInvalid)
            .trim(),
    });

export type RegistrationEmailFormData = yup.InferType<ReturnType<typeof createRegistrationEmailSchema>>;

export const createRegistrationCompanyDetailsSchema = (t: Translations) =>
    yup.object({
        companyName: yup
            .string()
            .required(t.registration.form.requiredField)
            .min(2, render(t.registration.form.minLengthTemplate, { n: 2 }))
            .max(100, render(t.registration.form.maxLengthTemplate, { n: 100 }))
            .matches(LATIN_TEXT_REGEX, t.registration.form.latinOnly)
            .trim(),
        activity: yup
            .string()
            .required(t.registration.form.requiredField)
            .min(4, render(t.registration.form.minLengthTemplate, { n: 4 }))
            .max(40, render(t.registration.form.maxLengthTemplate, { n: 40 }))
            .matches(LATIN_TEXT_REGEX, t.registration.form.latinOnly)
            .trim(),
        inn: yup
            .string()
            .defined()
            .max(50, render(t.registration.form.maxLengthTemplate, { n: 50 }))
            .trim(),
    });

export type RegistrationCompanyDetailsFormData = yup.InferType<
    ReturnType<typeof createRegistrationCompanyDetailsSchema>
>;

export const createRegistrationAddressDetailsSchema = (t: Translations) =>
    yup.object({
        country: yup.string().required(t.registration.form.requiredField).trim(),
        city: yup
            .string()
            .required(t.registration.form.requiredField)
            .min(2, render(t.registration.form.minLengthTemplate, { n: 2 }))
            .max(40, render(t.registration.form.maxLengthTemplate, { n: 40 }))
            .matches(LATIN_TEXT_REGEX, t.registration.form.latinOnly)
            .trim(),
        line: yup
            .string()
            .required(t.registration.form.requiredField)
            .min(5, render(t.registration.form.minLengthTemplate, { n: 5 }))
            .max(40, render(t.registration.form.maxLengthTemplate, { n: 40 }))
            .matches(LATIN_TEXT_REGEX, t.registration.form.latinOnly)
            .trim(),
        postcode: yup
            .string()
            .required(t.registration.form.requiredField)
            .min(2, render(t.registration.form.minLengthTemplate, { n: 2 }))
            .max(20, render(t.registration.form.maxLengthTemplate, { n: 20 }))
            .matches(LATIN_TEXT_REGEX, t.registration.form.latinOnly)
            .trim(),
    });

export type RegistrationAddressDetailsFormData = yup.InferType<
    ReturnType<typeof createRegistrationAddressDetailsSchema>
>;

export const createRegistrationPersonalDetailsSchema = (t: Translations) =>
    yup.object({
        firstName: yup
            .string()
            .required(t.registration.form.requiredField)
            .min(2, render(t.registration.form.minLengthTemplate, { n: 2 }))
            .max(40, render(t.registration.form.maxLengthTemplate, { n: 40 }))
            .matches(LATIN_NAME_REGEX, t.registration.form.latinOnly)
            .trim(),
        lastName: yup
            .string()
            .required(t.registration.form.requiredField)
            .min(2, render(t.registration.form.minLengthTemplate, { n: 2 }))
            .max(40, render(t.registration.form.maxLengthTemplate, { n: 40 }))
            .matches(LATIN_NAME_REGEX, t.registration.form.latinOnly)
            .trim(),
        gender: yup.string().required(t.registration.form.requiredField),
        phone: yup
            .string()
            .required(t.registration.form.requiredField)
            .matches(/^[0-9+()]+$/, t.registration.form.phoneInvalid)
            .min(5, render(t.registration.form.minLengthTemplate, { n: 5 }))
            .max(20, render(t.registration.form.maxLengthTemplate, { n: 20 }))
            .trim(),
    });

export type RegistrationPersonalDetailsFormData = yup.InferType<
    ReturnType<typeof createRegistrationPersonalDetailsSchema>
>;
